import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import ProCalculatorFooter from './ProCalculatorFooter';
import LoginGateModal from './LoginGateModal';
import * as XLSX from 'xlsx-js-style';
import { Check, ClipboardList, Download, FileSpreadsheet, Printer, AlertTriangle, Info } from 'lucide-react';
import '../styles/ResultsSection.css';
import AmazonLinksSection from './sections/AmazonLinksSection';
import { useAuth } from '../contexts/AuthContext';
import { logUsageEvent } from '../utils/logUsageEvent';
import { gaEvent } from '../utils/analytics';
import copyToClipboard from '../utils/copyToClipboard';
import { exportToPdf } from '../utils/exportToPdf';
import { GRAMS_PER_OZ, sumDetailAmountsForColor, sumAllDetailAmounts } from '../utils/calculatorUtils';
import { weightToDisplay, weightUnitLabel, lengthToDisplay, lengthUnitLabel, areaToDisplay, areaUnitLabel } from '../utils/unitConversion';

// Extra per-color icing categories shown below the main Flood totals.
// `key` matches the property name inside the plan's detailAmounts object.
const DETAIL_ROW_CONFIG = [
  { key: 'detail', label: 'Detail', tooltip: 'Icing for lettering or fine piping details, entered once per color. Added on top of the per-cookie totals above, not tied to any single cookie.' },
  { key: 'floral', label: 'Floral', tooltip: 'Icing for floral work, entered once per color. Added on top of the per-cookie totals above, not tied to any single cookie.' },
  { key: 'other', label: 'Other', tooltip: 'Icing for anything else, like wet-on-wet accents, entered once per color. Added on top of the per-cookie totals above, not tied to any single cookie.' },
];

const ResultsSection = () => {
  const { user } = useAuth();
  const {
    cookieRows,
    dimensionsRows,
    colorRows,
    colorNames,
    orderRows,
    useSets,
    setRows,
    isMetric,
    isMetricWeight,
    calculateColorIcing,
    getColorStyle,
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    isMobile,
    sectionCompletionStatus,
    loadedPlanName,
    loadedPlanId,
    reference,
    useDefaultReference,
    detailAmounts,
    setDetailAmounts,
    setActiveSection,
  } = useProCalculator();

  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);
  const [showExportChooser, setShowExportChooser] = useState(false);
  // Which Detail/Floral/Other info note is pinned open (tap/click) — null when none
  const [openInfoKey, setOpenInfoKey] = useState(null);

  // Single source of truth for all table data and totals
  const { resultRows, colorTotals, grandTotal } = useMemo(() => {
    const resultRows = [];
    const colorTotals = Array(colorNames.length).fill(0);
    let grandTotal = 0;

    if (useSets) {
      const setGroups = {};
      setRows.forEach(setRow => {
        if (!setRow.cookieId || !setRow.setName) return;
        if (!setGroups[setRow.setName]) setGroups[setRow.setName] = [];
        setGroups[setRow.setName].push(setRow);
      });
      Object.entries(setGroups).forEach(([setName, rows]) => {
        rows.forEach(setRow => {
          const cookie = cookieRows.find(c => c.id === setRow.cookieId);
          if (!cookie) return;
          const colorAmounts = calculateColorIcing(setRow.cookieId, setRow.count);
          let rowTotal = 0;
          colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
          grandTotal += rowTotal;
          resultRows.push({ id: `${setRow.id}_${setRow.cookieId}`, setName, cookieName: cookie.cookieName, colorAmounts, total: rowTotal });
        });
      });
    } else {
      cookieRows.forEach(cookie => {
        const orderRow = orderRows.find(o => o.cookieId === cookie.id);
        const count = orderRow ? Number(orderRow.count) : 0;
        const colorAmounts = calculateColorIcing(cookie.id, count);
        let rowTotal = 0;
        colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
        grandTotal += rowTotal;
        resultRows.push({ id: cookie.id, cookieName: cookie.cookieName, count, colorAmounts, total: rowTotal });
      });
    }

    return { resultRows, colorTotals, grandTotal };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }, [cookieRows, orderRows, setRows, colorNames, useSets, calculateColorIcing]);

  const handleExportPdf = async () => {
    if (!user) {
      setShowLoginGate(true);
      return;
    }
    // Await log before download — on iPad the download can kill pending network requests
    await logUsageEvent({ eventType: 'export_pdf', calculator: 'pro', user, planId: loadedPlanId, planName: loadedPlanName });
    gaEvent('export_pdf', { calculator: 'pro' });

    try {
      await exportToPdf({
        cookieRows,
        dimensionsRows,
        orderRows,
        setRows,
        colorNames,
        useSets,
        calculateColorIcing,
        loadedPlanName,
        isMetric,
        isMetricWeight,
        detailAmounts,
      });
    } catch (e) {
      console.warn('[handleExportPdf] download failed:', e.message);
    }

    setPdfSuccess(true);
    setTimeout(() => setPdfSuccess(false), 2500);
  };

  let shouldBlurResults = false;
  if (sectionCompletionStatus) {
    if (
      sectionCompletionStatus.reference === 'incomplete' ||
      sectionCompletionStatus.cookieNames === 'incomplete' ||
      sectionCompletionStatus.dimensions === 'incomplete' ||
      sectionCompletionStatus.colors === 'incomplete' ||
      (useSets && sectionCompletionStatus.sets === 'incomplete') ||
      (!useSets && sectionCompletionStatus.orders === 'incomplete')
    ) {
      shouldBlurResults = true;
    }
  }

  // Build detailed error messages for each incomplete section
  const blurMessages = useMemo(() => {
    if (!shouldBlurResults) return [];
    const msgs = [];
    const ordinal = (n) => {
      const s = ['th','st','nd','rd'], v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const isValidPos = (v) => { const n = parseFloat(v); return !isNaN(n) && n > 0; };

    // Reference (Get Started)
    if (sectionCompletionStatus?.reference === 'incomplete') {
      const ref = useDefaultReference ? {} : (reference || {});
      if (!useDefaultReference) {
        if (!isValidPos(ref.width))         msgs.push({ text: 'Get Started: Missing width.', section: 'reference' });
        if (!isValidPos(ref.length))        msgs.push({ text: 'Get Started: Missing length.', section: 'reference' });
        if (!isValidPos(ref.weightNoIcing)) msgs.push({ text: 'Get Started: Missing weight without icing.', section: 'reference' });
        if (!isValidPos(ref.weight))        msgs.push({ text: 'Get Started: Missing weight with icing.', section: 'reference' });
      } else {
        msgs.push({ text: 'Get Started: Section is incomplete.', section: 'reference' });
      }
    }

    // Cookie Names
    if (sectionCompletionStatus?.cookieNames === 'incomplete') {
      (cookieRows || []).forEach((row, idx) => {
        const name = row.cookieName ? String(row.cookieName).trim() : '';
        if (!name || name === 'Cookie Name') {
          msgs.push({ text: `Cookie Names: Missing cookie name for the ${ordinal(idx + 1)} cookie.`, section: 'cookieNames' });
        }
      });
    }

    // Size & Shape
    if (sectionCompletionStatus?.dimensions === 'incomplete') {
      (dimensionsRows || []).forEach(row => {
        const cookie = (cookieRows || []).find(c => c.id === row.cookieId);
        const label = (cookie?.cookieName && cookie.cookieName !== 'Cookie Name')
          ? `"${cookie.cookieName}"`
          : 'an unnamed cookie';
        if (!row.shapeType) {
          msgs.push({ text: `Size & Shape: Missing shape type for ${label}.`, section: 'dimensions' });
        } else if (row.shapeType === 'circle' && !isValidPos(row.diameter)) {
          msgs.push({ text: `Size & Shape: Missing diameter for ${label}.`, section: 'dimensions' });
        } else if (row.shapeType === 'square' && !isValidPos(row.width)) {
          msgs.push({ text: `Size & Shape: Missing side length for ${label}.`, section: 'dimensions' });
        } else if ((row.shapeType === 'rectangle' || row.shapeType === 'triangle') && (!isValidPos(row.width) || !isValidPos(row.length))) {
          msgs.push({ text: `Size & Shape: Missing dimensions for ${label}.`, section: 'dimensions' });
        } else if ((row.shapeType === 'override' || row.shapeType === 'photo') && !isValidPos(row.overrideArea)) {
          msgs.push({ text: `Size & Shape: Missing area for ${label} — please analyze or enter the area.`, section: 'dimensions' });
        }
      });
    }

    // Colors (only flagged when 3+ colors)
    if (sectionCompletionStatus?.colors === 'incomplete') {
      (colorRows || []).forEach(row => {
        const cookie = (cookieRows || []).find(c => c.id === row.cookieId);
        const label = (cookie?.cookieName && cookie.cookieName !== 'Cookie Name')
          ? `"${cookie.cookieName}"`
          : 'an unnamed cookie';
        const percentages = row.percentages || [];
        const sum = percentages.reduce((acc, p) => acc + (Number(p) || 0), 0);
        if (Math.abs(sum - 100) > 0.01) {
          msgs.push({ text: `Colors: ${label} has color percentages totaling ${Math.round(sum)}% — they must equal 100%.`, section: 'colors' });
        }
      });
    }

    // Cookie Sets (only relevant when Use Cookie Sets is on)
    if (useSets && sectionCompletionStatus?.sets === 'incomplete') {
      msgs.push({ text: 'Sets: Every set needs a cookie selected.', section: 'sets' });
    }

    // How Many (only relevant when not using Sets)
    if (!useSets && sectionCompletionStatus?.orders === 'incomplete') {
      msgs.push({ text: 'How Many: Every cookie is still at 0. Enter a count for at least one cookie.', section: 'orders' });
    }

    return msgs;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }, [shouldBlurResults, sectionCompletionStatus, reference, useDefaultReference, cookieRows, dimensionsRows, colorRows, useSets]);

  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  // Function to copy results to clipboard
  const handleCopyResults = () => {
    const pad = (str, width) => String(str).padEnd(width);

    // Use the already-computed resultRows / colorTotals / grandTotal from useMemo
    // Determine column widths dynamically
    const MIN_NAME_W = 14;
    const MIN_COLOR_W = 10;
    const MIN_SET_W = 12;
    const MIN_TOTAL_W = 12;

    const nameW = Math.max(MIN_NAME_W, ...resultRows.map(r => r.cookieName.length)) + 2;
    const setW  = useSets ? Math.max(MIN_SET_W, ...resultRows.map(r => (r.setName || '').length)) + 2 : 0;
    const colorW = Math.max(MIN_COLOR_W, ...colorNames.map(c => c.length), 9 /* "999.99 g" */) + 2;
    const totalW = MIN_TOTAL_W;

    // Header line
    let resultsText = 'Cookie Icing Calculator Results\n';
    if (loadedPlanName) resultsText += `Plan: ${loadedPlanName}\n`;
    resultsText += `Date: ${new Date().toLocaleDateString()}\n\n`;

    // Column headers
    let header = '';
    if (useSets) header += pad('Set Name', setW);
    header += pad('Cookie Name', nameW);
    colorNames.forEach(color => { header += pad(color, colorW); });
    header += pad('Total', totalW);
    resultsText += header.trimEnd() + '\n';

    // Separator
    const sepLen = (useSets ? setW : 0) + nameW + colorW * colorNames.length + totalW;
    resultsText += '-'.repeat(sepLen) + '\n';

    // Data rows
    const wLabel = weightUnitLabel(isMetricWeight);
    resultRows.forEach(row => {
      let line = '';
      if (useSets) line += pad(row.setName || '', setW);
      line += pad(row.cookieName, nameW);
      row.colorAmounts.forEach(amount => { line += pad(`${weightToDisplay(amount, isMetricWeight).toFixed(2)} ${wLabel}`, colorW); });
      line += pad(`${weightToDisplay(row.total, isMetricWeight).toFixed(2)} ${wLabel}`, totalW);
      resultsText += line.trimEnd() + '\n';
    });

    // Total Flood row
    resultsText += '-'.repeat(sepLen) + '\n';
    let totalsLine = '';
    if (useSets) totalsLine += pad('', setW);
    totalsLine += pad(`Total Flood (${wLabel})`, nameW);
    colorTotals.forEach(total => { totalsLine += pad(`${weightToDisplay(total, isMetricWeight).toFixed(2)} ${wLabel}`, colorW); });
    totalsLine += pad(`${weightToDisplay(grandTotal, isMetricWeight).toFixed(2)} ${wLabel}`, totalW);
    resultsText += totalsLine.trimEnd() + '\n';

    const wLabel2 = weightUnitLabel(!isMetricWeight);

    // Detail / Floral / Other rows
    DETAIL_ROW_CONFIG.forEach(({ key, label }) => {
      const rowSumGrams = (detailAmounts?.[key] || []).reduce((s, g) => s + (Number(g) || 0), 0);
      let rowLine = '';
      if (useSets) rowLine += pad('', setW);
      rowLine += pad(label, nameW);
      colorNames.forEach((_, i) => {
        const dg = Number(detailAmounts?.[key]?.[i]) || 0;
        rowLine += pad(`${weightToDisplay(dg, isMetricWeight).toFixed(2)} ${wLabel}`, colorW);
      });
      rowLine += pad(`${weightToDisplay(rowSumGrams, isMetricWeight).toFixed(2)} ${wLabel}`, totalW);
      resultsText += rowLine.trimEnd() + '\n';
    });

    // Flood + Extras row
    const detailSumGrams = sumAllDetailAmounts(detailAmounts);
    let tpdLine = '';
    if (useSets) tpdLine += pad('', setW);
    tpdLine += pad(`Flood+Extras (${wLabel})`, nameW);
    colorTotals.forEach((total, i) => {
      const combined = total + sumDetailAmountsForColor(detailAmounts, i);
      tpdLine += pad(`${weightToDisplay(combined, isMetricWeight).toFixed(2)} ${wLabel}`, colorW);
    });
    const grandPlusDetail = grandTotal + detailSumGrams;
    tpdLine += pad(`${weightToDisplay(grandPlusDetail, isMetricWeight).toFixed(2)} ${wLabel}`, totalW);
    resultsText += tpdLine.trimEnd() + '\n\n';

    // Color summary (Flood + Extras)
    resultsText += 'Color Summary (Flood + Extras):\n';
    colorNames.forEach((color, index) => {
      const gramsVal = (colorTotals[index] ?? 0) + sumDetailAmountsForColor(detailAmounts, index);
      const primaryVal = weightToDisplay(gramsVal, isMetricWeight).toFixed(2);
      resultsText += `${color}: ${primaryVal} ${wLabel}\n`;
    });

    // Attribution
    resultsText += '\n---\n';
    resultsText += 'Results provided by TheIcingCalculator.com';
    
    // Log immediately — user clearly intended the action regardless of clipboard API result
    logUsageEvent({ eventType: 'copy_results', calculator: 'pro', user, planId: loadedPlanId, planName: loadedPlanName });
    gaEvent('copy_results', { calculator: 'pro' });

    // Copy to clipboard — always show feedback (iOS may report false on repeat clicks)
    copyToClipboard(resultsText).then(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // Helper: format cookie dimensions into a single display string
  const formatDimsCell = (cookieId) => {
    const dim = dimensionsRows.find(d => d.cookieId === cookieId);
    if (!dim || !dim.shapeType) return '';
    const lbl = lengthUnitLabel(isMetric);
    const shape = dim.shapeType.charAt(0).toUpperCase() + dim.shapeType.slice(1);
    if (dim.shapeType === 'circle') {
      const d = lengthToDisplay(dim.diameter, isMetric);
      return d ? `${shape} ${d} ${lbl}` : shape;
    }
    if (dim.shapeType === 'square') {
      const s = lengthToDisplay(dim.width, isMetric);
      return s ? `${shape} ${s}x${s} ${lbl}` : shape;
    }
    if (dim.shapeType === 'override' || dim.shapeType === 'photo') {
      const a = dim.overrideArea ? areaToDisplay(dim.overrideArea, isMetric) : null;
      return a ? `${shape}\n${a} ${areaUnitLabel(isMetric)}` : shape;
    }
    const w = lengthToDisplay(dim.width, isMetric);
    const l = lengthToDisplay(dim.length, isMetric);
    return (w && l) ? `${shape} ${w}x${l} ${lbl}` : shape;
  };

  // Function to export results to Excel
  const exportToExcel = async () => {
    if (!user) {
      setShowLoginGate(true);
      return;
    }
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Create data array for the worksheet
    const data = [];
    const xlPrimaryLabel = weightUnitLabel(isMetricWeight);
    const xlAltLabel = weightUnitLabel(!isMetricWeight);
    
    // Add title
    data.push(['Cookie Icing Calculator Results']);
    
    // Add plan name if loaded
    if (loadedPlanName) {
      data.push([`Plan: ${loadedPlanName}`]);
    }
    
    // Add date
    data.push([`Date: ${new Date().toLocaleDateString()}`]);
    data.push([]);
    
    // Add results table header
    // Columns: [Set Name?] | Cookie Name | How Many | Dimensions | ...colors... | Total (primary) | Total (alt)
    const header = [];
    if (useSets) header.push('Set Name');
    header.push('Cookie Name', 'How Many', 'Dimensions');
    colorNames.forEach(color => header.push(color));
    header.push(`Total (${xlPrimaryLabel})`);
    data.push(header);
    
    // Process the results
    const xlResultRows = [];
    const colorTotals = Array(colorNames.length).fill(0);
    let grandTotal = 0;
    
    if (useSets) {
      const setGroups = {};
      setRows.forEach(setRow => {
        if (!setRow.cookieId || !setRow.setName) return;
        if (!setGroups[setRow.setName]) setGroups[setRow.setName] = [];
        setGroups[setRow.setName].push(setRow);
      });
      
      Object.entries(setGroups).forEach(([setName, rows]) => {
        rows.forEach(setRow => {
          const cookie = cookieRows.find(c => c.id === setRow.cookieId);
          if (!cookie) return;
          const count = setRow.count;
          const colorAmounts = calculateColorIcing(setRow.cookieId, count);
          let rowTotal = 0;
          colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
          grandTotal += rowTotal;
          xlResultRows.push({ setName, cookieId: setRow.cookieId, cookieName: cookie.cookieName, colorAmounts, total: rowTotal });
        });
      });
    } else {
      cookieRows.forEach(cookie => {
        const orderRow = orderRows.find(o => o.cookieId === cookie.id);
        const count = orderRow ? Number(orderRow.count) : 0;
        const colorAmounts = calculateColorIcing(cookie.id, count);
        let rowTotal = 0;
        colorAmounts.forEach((amount, i) => { rowTotal += amount; colorTotals[i] += amount; });
        grandTotal += rowTotal;
        xlResultRows.push({ cookieId: cookie.id, cookieName: cookie.cookieName, count, colorAmounts, total: rowTotal });
      });
    }
    
    // Add data rows
    xlResultRows.forEach(row => {
      const rowData = [];
      if (useSets) rowData.push(row.setName);
      rowData.push(row.cookieName, row.count ?? '', formatDimsCell(row.cookieId));
      row.colorAmounts.forEach(amount => {
        rowData.push(`${weightToDisplay(amount, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
      });
      rowData.push(`${weightToDisplay(row.total, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
      data.push(rowData);
    });
    
    // Add totals row
    const totalsRow = [`Total Flood (${xlPrimaryLabel})`];
    if (useSets) totalsRow.push('');
    totalsRow.push('', ''); // How Many col + dimensions col
    colorTotals.forEach(total => { totalsRow.push(`${weightToDisplay(total, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`); });
    totalsRow.push(`${weightToDisplay(grandTotal, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
    data.push(totalsRow);

    // Detail / Floral / Other rows
    DETAIL_ROW_CONFIG.forEach(({ key, label }) => {
      const rowSumGrams = (detailAmounts?.[key] || []).reduce((s, g) => s + (Number(g) || 0), 0);
      const xlRow = [label];
      if (useSets) xlRow.push('');
      xlRow.push('', ''); // How Many + Dimensions
      colorNames.forEach((_, i) => {
        const dg = Number(detailAmounts?.[key]?.[i]) || 0;
        xlRow.push(`${weightToDisplay(dg, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
      });
      xlRow.push(`${weightToDisplay(rowSumGrams, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
      data.push(xlRow);
    });

    // Flood + Extras row
    const xlDetailSumGrams = sumAllDetailAmounts(detailAmounts);
    const xlGrandPlusDetail = grandTotal + xlDetailSumGrams;
    const xlTpdRow = [`Flood+Extras (${xlPrimaryLabel})`];
    if (useSets) xlTpdRow.push('');
    xlTpdRow.push('', ''); // How Many + Dimensions
    colorTotals.forEach((total, i) => {
      const combined = total + sumDetailAmountsForColor(detailAmounts, i);
      xlTpdRow.push(`${weightToDisplay(combined, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
    });
    xlTpdRow.push(`${weightToDisplay(xlGrandPlusDetail, isMetricWeight).toFixed(2)} ${xlPrimaryLabel}`);
    data.push(xlTpdRow);

    data.push([]);

    // Add color summary (Flood + Extras)
    data.push(['COLOR SUMMARY (Flood + Extras)']);

    colorNames.forEach((color, index) => {
      const gramsVal = (colorTotals[index] ?? 0) + sumDetailAmountsForColor(detailAmounts, index);
      const primaryVal = weightToDisplay(gramsVal, isMetricWeight).toFixed(2);
      data.push([color, `${primaryVal} ${xlPrimaryLabel}`]);
    });
    
    // Add attribution
    data.push([]);
    data.push(['---']);
    data.push(['Results provided by TheIcingCalculator.com']);
    
    // Create worksheet from data
    const ws = XLSX.utils.aoa_to_sheet(data);

    // ── Excel cell styling ──────────────────────────────────────────
    let headerRowIdx = 3; // title + date + blank
    if (loadedPlanName) headerRowIdx = 4; // +plan name row
    const dataRowCount = xlResultRows.length;
    const totalsGRowIdx      = headerRowIdx + 1 + dataRowCount;
    const detailRowIdx       = totalsGRowIdx + 1;
    const floralRowIdx       = detailRowIdx + 1;
    const otherRowIdx        = floralRowIdx + 1;
    const tpdRowIdx          = otherRowIdx + 1;
    const colorSummaryHeaderIdx = tpdRowIdx + 2; // +blank row
    const colorSummaryEnd = colorSummaryHeaderIdx + colorNames.length;

    // Number of columns in the main table: [Set?] + Cookie Name + How Many + Dimensions + colors + Total
    const mainColCount = (useSets ? 1 : 0) + 3 + colorNames.length + 1;

    const GREY_FILL  = { patternType: 'solid', fgColor: { rgb: 'D9D9D9' } };
    const BORDER_CELL = {
      top:    { style: 'thin', color: { rgb: '888888' } },
      bottom: { style: 'thin', color: { rgb: '888888' } },
      left:   { style: 'thin', color: { rgb: '888888' } },
      right:  { style: 'thin', color: { rgb: '888888' } },
    };

    const applyStyle = (rowIdx, colCount, style) => {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = { ...(ws[addr].s || {}), ...style };
      }
    };

    const applyBorderAndFill = (rowIdx, colCount, fill) => {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = {
          ...(ws[addr].s || {}),
          border: BORDER_CELL,
          ...(fill ? { fill } : {}),
          font: { bold: fill ? true : false },
        };
      }
    };

    // Header row — shaded + bold + bordered
    applyBorderAndFill(headerRowIdx, mainColCount, GREY_FILL);

    // Data rows — bordered only (no fill)
    for (let r = headerRowIdx + 1; r < totalsGRowIdx; r++) {
      applyBorderAndFill(r, mainColCount, null);
    }

    // Dimensions column — wrap text so multi-line cells (e.g. Override area) render on separate lines
    const dimsColIdx = (useSets ? 1 : 0) + 2;
    for (let r = headerRowIdx + 1; r < totalsGRowIdx; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: dimsColIdx });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = { ...(ws[addr].s || {}), alignment: { wrapText: true, vertical: 'top' } };
    }

    // Total Flood row — shaded + bold + bordered
    applyBorderAndFill(totalsGRowIdx, mainColCount, GREY_FILL);

    // Detail / Floral / Other rows — light green tint
    const GREEN_FILL = { patternType: 'solid', fgColor: { rgb: 'D6EDD6' } };
    applyBorderAndFill(detailRowIdx, mainColCount, GREEN_FILL);
    applyBorderAndFill(floralRowIdx, mainColCount, GREEN_FILL);
    applyBorderAndFill(otherRowIdx, mainColCount, GREEN_FILL);

    // Flood + Extras row — light blue tint + bold
    const BLUE_FILL = { patternType: 'solid', fgColor: { rgb: 'C9DEFF' } };
    applyBorderAndFill(tpdRowIdx, mainColCount, BLUE_FILL);

    // Color summary header row — shaded + bold + bordered (2 cols)
    applyStyle(colorSummaryHeaderIdx, 2, { fill: GREY_FILL, border: BORDER_CELL, font: { bold: true } });

    // Color summary data rows — bordered (2 cols)
    for (let r = colorSummaryHeaderIdx + 1; r <= colorSummaryEnd; r++) {
      applyBorderAndFill(r, 2, null);
    }
    // ── End styling ─────────────────────────────────────────────────

    // Set column widths dynamically
    const colWidths = [];
    if (useSets) colWidths.push({ wch: 20 }); // Set Name
    colWidths.push({ wch: 28 }); // Cookie Name
    colWidths.push({ wch: 12 }); // How Many
    colWidths.push({ wch: 26 }); // Dimensions
    colorNames.forEach(() => colWidths.push({ wch: 14 }));
    colWidths.push({ wch: 20 }); // Total Flood
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Icing Calculations');
    
    // Await log before download — on iPad the download can kill pending network requests
    await logUsageEvent({ eventType: 'export_excel', calculator: 'pro', user, planId: loadedPlanId, planName: loadedPlanName });
    gaEvent('export_excel', { calculator: 'pro' });

    // Generate Excel file and trigger download
    const date = new Date().toISOString().split('T')[0];
    try {
      XLSX.writeFile(wb, `Icing_Calculator_Results_${date}.xlsx`);
    } catch (e) {
      console.warn('[exportToExcel] download failed:', e.message);
    }

    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 2000);
  };

  return (
    <div className="results-section">
      <div className="section-header">
        <h2 className="section-title">My Icing Plan</h2>
        {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
      </div>
      
      
      {/* Help text container added */}
      <div className="section-help-container">
        <SectionHelp sectionId="results" isMobile={isMobile} />
      </div>


      <div className={`results-content-wrapper ${shouldBlurResults ? 'blur-active' : ''}`}>
        {shouldBlurResults && (
          <div className="results-blur-overlay">
            <p className="results-blur-heading">Not all sections are complete. Please review the following:</p>
            <ul className="results-blur-list">
              {blurMessages.map((msg, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="results-blur-jump"
                    onClick={() => setActiveSection(msg.section)}
                  >
                    {msg.text} <span className="results-blur-jump-arrow" aria-hidden="true">→</span>
                  </button>
                </li>
              ))}
              {blurMessages.length === 0 && (
                <li>Review any section showing a red circle number in the menu.</li>
              )}
            </ul>
          </div>
        )}

        {/* Results Table */}
        <div className="results-table">
          <div className="results-table-header">
            {useSets && <div className="set-name-header">Set Name</div>}
            <div className="cookie-name-header">Cookie Name</div>
            {!useSets && <div className="count-header">How Many</div>}
            
            {colorNames.map((color, index) => (
              <div key={index} className="color-amount-header">
                <div className="color-sample" style={getColorStyle(color)}></div>
                <span>{color}</span>
              </div>
            ))}
            
            <div className="total-header">Total ({weightUnitLabel(isMetricWeight)})</div>
          </div>
          
          {/* Data rows */}
          {resultRows.map(row => (
            <div key={row.id} className="results-row">
              {useSets && <div className="set-name-field">{row.setName}</div>}
              <div className="cookie-name-field">{row.cookieName}</div>
              {!useSets && (
                Number(row.count) > 0 ? (
                  <div className="count-field">{row.count}</div>
                ) : (
                  <div className="count-field count-zero" title="This cookie has a count of 0 — it won't add anything to the totals below.">
                    <AlertTriangle size={14} aria-hidden="true" />0
                  </div>
                )
              )}
              {row.colorAmounts.map((amount, index) => (
                <div key={index} className="color-amount-field">
                  {(Number(weightToDisplay(amount, isMetricWeight)) || 0).toFixed(2)} {weightUnitLabel(isMetricWeight)}
                </div>
              ))}
              <div className="total-field">
                {(Number(weightToDisplay(row.total, isMetricWeight)) || 0).toFixed(2)} {weightUnitLabel(isMetricWeight)}
              </div>
            </div>
          ))}

          {/* Totals row */}
          <div className="results-row totals-row">
            <div className={`totals-label-field ${useSets ? 'with-sets' : ''}`}>
              Total Flood
            </div>
            {!useSets && <div className="count-field count-total">{resultRows.reduce((sum, r) => sum + (Number(r.count) || 0), 0)}</div>}
            {colorTotals.map((total, index) => (
              <div key={index} className="color-amount-field total-value">
                <div>{weightToDisplay(total, isMetricWeight).toFixed(2)} {weightUnitLabel(isMetricWeight)}</div>
                <div className="total-oz">({weightToDisplay(total, !isMetricWeight).toFixed(2)} {weightUnitLabel(!isMetricWeight)})</div>
              </div>
            ))}
            <div className="total-field grand-total">
              <div>{weightToDisplay(grandTotal, isMetricWeight).toFixed(2)} {weightUnitLabel(isMetricWeight)}</div>
              <div className="total-oz">({weightToDisplay(grandTotal, !isMetricWeight).toFixed(2)} {weightUnitLabel(!isMetricWeight)})</div>
            </div>
          </div>

          {/* Detail / Floral / Other rows */}
          {DETAIL_ROW_CONFIG.map(({ key, label, tooltip }) => (
            <React.Fragment key={key}>
            <div className="results-row detail-row">
              <div
                className={`totals-label-field detail-label ${useSets ? 'with-sets' : ''}`}
                role="button"
                tabIndex={0}
                aria-expanded={openInfoKey === key}
                aria-label={`About ${label}`}
                onClick={() => setOpenInfoKey(prev => (prev === key ? null : key))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenInfoKey(prev => (prev === key ? null : key));
                  }
                }}
              >
                {label}
                <Info size={14} className="detail-label-info" aria-hidden="true" />
                <span className="detail-tooltip" role="tooltip">{tooltip}</span>
              </div>
              {!useSets && <div className="count-field"></div>}
              {colorNames.map((_, index) => {
                const gramsVal = Number(detailAmounts?.[key]?.[index]) || 0;
                const displayVal = weightToDisplay(gramsVal, isMetricWeight);
                return (
                  <div key={index} className="color-amount-field detail-input-cell">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={displayVal === 0 ? '0' : displayVal}
                      onClick={e => e.target.select()}
                      onChange={e => {
                        const raw = parseFloat(e.target.value) || 0;
                        // Convert display unit back to grams for storage
                        const inGrams = isMetricWeight ? raw : raw * GRAMS_PER_OZ;
                        setDetailAmounts(prev => {
                          const nextCategory = [...(prev?.[key] || [])];
                          while (nextCategory.length <= index) nextCategory.push(0);
                          nextCategory[index] = inGrams;
                          return { ...prev, [key]: nextCategory };
                        });
                      }}
                      className="detail-amount-input"
                    />
                    <span className="detail-unit">{weightUnitLabel(isMetricWeight)}</span>
                  </div>
                );
              })}
              <div className="total-field detail-total">
                {(() => {
                  const rowSum = (detailAmounts?.[key] || []).reduce((s, g) => s + (Number(g) || 0), 0);
                  return weightToDisplay(rowSum, isMetricWeight).toFixed(2) + ' ' + weightUnitLabel(isMetricWeight);
                })()}
              </div>
            </div>
            {openInfoKey === key && (
              <div className="detail-info-banner">
                <Info size={16} aria-hidden="true" />
                <span>{tooltip}</span>
                <button
                  type="button"
                  className="detail-info-banner-close"
                  aria-label="Close"
                  onClick={() => setOpenInfoKey(null)}
                >
                  ×
                </button>
              </div>
            )}
            </React.Fragment>
          ))}

          {/* Flood + Extras row */}
          <div className="results-row total-plus-detail-row">
            <div className={`totals-label-field total-plus-detail-label ${useSets ? 'with-sets' : ''}`}>
              Flood + Extras
            </div>
            {!useSets && <div className="count-field"></div>}
            {colorTotals.map((total, index) => {
              const detailGrams = sumDetailAmountsForColor(detailAmounts, index);
              const combined = total + detailGrams;
              return (
                <div key={index} className="color-amount-field total-plus-detail-value">
                  <div>{weightToDisplay(combined, isMetricWeight).toFixed(2)} {weightUnitLabel(isMetricWeight)}</div>
                  <div className="total-oz">({weightToDisplay(combined, !isMetricWeight).toFixed(2)} {weightUnitLabel(!isMetricWeight)})</div>
                </div>
              );
            })}
            <div className="total-field grand-total-plus-detail">
              {(() => {
                const detailSum = sumAllDetailAmounts(detailAmounts);
                const combined = grandTotal + detailSum;
                return (
                  <>
                    <div>{weightToDisplay(combined, isMetricWeight).toFixed(2)} {weightUnitLabel(isMetricWeight)}</div>
                    <div className="total-oz">({weightToDisplay(combined, !isMetricWeight).toFixed(2)} {weightUnitLabel(!isMetricWeight)})</div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        
        {/* Results action buttons — Save/Clear left, export buttons right */}
        <div className="button-container results-button-row">
          <ProCalculatorFooter
            resultsModeOnly
            onBack={() => setActiveSection('orders')}
            backLabel="Back: How Many"
          />
          <div className="results-export-buttons">
            <button 
              className={`copy-button ${copySuccess ? 'copied' : ''}`}
              onClick={handleCopyResults}
              title="Copy results to clipboard"
            >
              {copySuccess
                ? <><Check size={18} />Copied!</>
                : <><ClipboardList size={18} />Copy Icing Totals</>}
            </button>
            
            {isMobile ? (
              <div className="export-chooser-wrapper">
                <button
                  className={`export-button ${(exportSuccess || pdfSuccess) ? 'exported' : ''}`}
                  onClick={() => setShowExportChooser(v => !v)}
                  title="Export results"
                >
                  {(exportSuccess || pdfSuccess)
                    ? <><Check size={18} />Downloaded!</>
                    : <><Download size={18} />Export</>}
                </button>
                {showExportChooser && (
                  <div className="export-chooser">
                    <button
                      className={`export-chooser-item ${exportSuccess ? 'exported' : ''}`}
                      onClick={() => { setShowExportChooser(false); exportToExcel(); }}
                    >
                      <FileSpreadsheet size={18} />Excel
                    </button>
                    <button
                      className={`export-chooser-item ${pdfSuccess ? 'exported' : ''}`}
                      onClick={() => { setShowExportChooser(false); handleExportPdf(); }}
                    >
                      <Printer size={18} />PDF
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  className={`excel-button ${exportSuccess ? 'exported' : ''}`}
                  onClick={exportToExcel}
                  title="Export results to Excel"
                >
                  {exportSuccess
                    ? <><Check size={18} />Downloaded!</>
                    : <><FileSpreadsheet size={18} />Export to Excel</>}
                </button>
                <button
                  className={`pdf-button ${pdfSuccess ? 'exported' : ''}`}
                  onClick={handleExportPdf}
                  title="Download as print-ready PDF"
                >
                  {pdfSuccess
                    ? <><Check size={18} />Downloaded!</>
                    : <><Printer size={18} />Export to PDF</>}
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Color summary section */}
        <div className="color-summary">
          <h3>Color Summary</h3>
          <div className="color-summary-list">
            {colorNames.map((color, index) => {
              const floodGrams = colorTotals[index] ?? 0;
              const detailGrams = sumDetailAmountsForColor(detailAmounts, index);
              const gramsValue = floodGrams + detailGrams;
              const primaryValue = weightToDisplay(gramsValue, isMetricWeight);
              const secondaryValue = weightToDisplay(gramsValue, !isMetricWeight);
              
              return (
                <div key={index} className="color-summary-item">
                  <div className="color-sample" style={getColorStyle(color)}></div>
                  <div className="color-summary-content">
                    <div className="color-name">{color}</div>
                    <div className="color-measurements">
                      <span className="measurement">{primaryValue.toFixed(2)} {weightUnitLabel(isMetricWeight)}</span>
                      <span className="measurement-separator">|</span>
                      <span className="measurement">{secondaryValue.toFixed(2)} {weightUnitLabel(!isMetricWeight)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div> {/* End of results-content-wrapper */}
      
      {/* Recommended Tools Section */}
      <AmazonLinksSection title="Recommended Decorating Tools" />

      <LoginGateModal
        open={showLoginGate}
        onClose={() => setShowLoginGate(false)}
        action="export your icing plan"
      />
    </div>
  );
};

export default ResultsSection;
